/* eslint-disable class-methods-use-this */
import React from 'react';
import BaseClass from 'components/BaseClass';
import { wrapComponent } from 'utils/framework';
import { Button } from 'components/ui';
import ApplePayButton from 'components/ApplePayButton/ApplePayButton';
import checkoutApi from 'services/api/checkout';
import Debounce from 'utils/Debounce';
import OrderUtils from 'utils/Order';
import checkoutUtils from 'utils/Checkout';
import decorators from 'utils/decorators';
import {
    prepareCheckoutPlaceOrderLinkshareInformation,
    syncBopisDcAttributionAfterPlaceOrderSuccess
} from 'utils/CreatorStoreFront/dynamicCommissioning';
import { INTERSTICE_DELAY_MS } from 'constants/Checkout';
import processEvent from 'analytics/processEvent';
import anaConsts from 'analytics/constants';
import analyticsUtils from 'analytics/utils';
import basketUtils from 'utils/Basket';
import CheckoutBindings from 'analytics/bindingMethods/pages/checkout/CheckoutBindings';
import FreePaymentButton from 'components/FrictionlessCheckout/PlaceOrderButton/FreePaymentButton/FreePaymentButton';
import PayPalButton from 'components/FrictionlessCheckout/PlaceOrderButton/PayPalButton/PayPalButton.f';
import Venmo from 'utils/Venmo';
import PayPalCheckout from 'utils/PayPalCheckout';
import GooglePay from 'utils/GooglePay';
import GooglePayButton from 'components/GooglePayButton/GooglePayButton';
import GooglePayEN from 'utils/locales/GooglePay_en_US';
import GooglePayFR from 'utils/locales/GooglePay_fr_CA';
import LanguageLocale from 'utils/LanguageLocale';
import store from 'store/Store';
import Actions from 'Actions';
import FrictionlessUtils from 'utils/FrictionlessCheckout';
import ErrorsUtils from 'utils/Errors';
import { isKlarnaFallbackAuthFailure } from 'utils/klarnaAuthGuard';
import FrictionlessCheckoutBindings from 'analytics/bindingMethods/pages/FrictionlessCheckout/FrictionlessCheckoutBindings';
import UI from 'utils/UI';
import agentAwareUtils from 'utils/AgentAware';
import { refreshCheckout } from 'services/Checkout';
import paymentUtils from 'utils/payment';
import Storage from 'utils/localStorage/Storage';
import LOCAL_STORAGE from 'utils/localStorage/Constants';
import { buildShippingSpeedSOTPayload } from 'analytics/utils/shippingSpeedTracking';

const { checkForCheckoutSectionsCompleteness, getErrorStatesFromSubmitOrderAPI, isFCFinalReleaseEnabled } = FrictionlessUtils;

class PlaceOrderButton extends BaseClass {
    constructor(props) {
        super(props);
        this.state = {
            isVenmoFlow: false,
            isProcessingOrder: false,
            orderError: null
        };
    }

    componentDidMount() {
        const isVenmoFlow = Venmo.isVenmoExpressFlow();

        this.setState({
            isVenmoFlow
        });

        const originalPlaceOrder = this.placeOrder;
        this.placeOrder = (e, data) => {
            if (this.props.placeOrderPreHook) {
                this.props.placeOrderPreHook(e).then(originalPlaceOrder);
            } else if (this.props.isAutoReplenishBasket && !this.props.acceptAutoReplenish) {
                this.props.showPlaceOrderTermsModal(true, !this.props.isSDUItemInBasket);
            } else if (this.props.isSDUItemInBasket && !this.props.sduAcceptTerms) {
                this.triggerSDUModal();
            } else {
                originalPlaceOrder(data);
            }

            if (this.props.isAutoReplenishBasket && this.props.acceptAutoReplenish) {
                this.props.showPlaceOrderTermsModal(false);
            }

            if (this.props.isSDUItemInBasket && this.props.sduAcceptTerms) {
                this.props.closeSDUModal();
            }
        };

        if (Sephora.isAgent && agentAwareUtils.isAgentAwareSession()) {
            //If it is Sephora Mirror, agent should see place order button disabled. However, certain roles should see enabled button only for card

            if (!agentAwareUtils.isAgentAuthorized(['3']) && !OrderUtils.isZeroCheckout()) {
                const updatedOrderDetails = {
                    ...this.props.orderDetails,
                    header: {
                        ...this.props.orderDetails?.header,
                        isComplete: false
                    }
                };
                this.props.updateOrder(updatedOrderDetails);
            }
        }

        this.placeOrder = Debounce.preventDoubleClick(this.placeOrder);

        if (isFCFinalReleaseEnabled()) {
            this.onPlaceOrderBtnClick = Debounce.preventDoubleClick(this.onPlaceOrderBtnClick);
        }
    }

    componentDidUpdate(prevProps) {
        const {
            isKlarnaSelected,
            isAfterpaySelected,
            isPazeSelected,
            isVenmoSelected,
            isPaypalSelected,
            isGooglePaySelected,
            togglePlaceOrderDisabled
        } = this.props;

        if (
            (prevProps.isKlarnaSelected && !isKlarnaSelected) ||
            (prevProps.isAfterpaySelected && !isAfterpaySelected) ||
            (prevProps.isPazeSelected && !isPazeSelected) ||
            (prevProps.isVenmoSelected && !isVenmoSelected) ||
            (prevProps.isPaypalSelected && !isPaypalSelected) ||
            (prevProps.isGooglePaySelected && !isGooglePaySelected)
        ) {
            !basketUtils.isZeroCheckout() && togglePlaceOrderDisabled(true);
        }
    }

    handleVenmoClick = () => {
        Venmo.checkoutWithVenmo({ defaultPayment: this.props.defaultPaymentName, callback: this.onPlaceOrderSuccess });
    };

    handleGooglePayClick = async () => {
        const errors = checkForCheckoutSectionsCompleteness(this.props.orderDetails, false, this.props.sectionThatNeedsToBeConfirmed);

        if (Object.keys(errors).length > 0) {
            this.props.setCheckoutSectionErrors(errors);
            UI.scrollTo({ elementId: Object.keys(errors)[0], hasOffset: false });

            return;
        }

        const { defaultPaymentName, existingDefaultPayment } = this.props;
        const isGooglePayDefaultPayment =
            paymentUtils.normalizePaymentName(defaultPaymentName) === 'googlepay' ||
            paymentUtils.normalizePaymentName(existingDefaultPayment) === 'googlepay';

        FrictionlessCheckoutBindings.trackPaymentVendorInteraction('checkout:payment:continue with googlepay', {
            isDefaultPayment: isGooglePayDefaultPayment
        });

        try {
            const result = await GooglePay.openCheckoutPaysheet({
                orderId: OrderUtils.getOrderId(),
                transactionAmount: basketUtils.removeCurrency(this.props.orderTotal),
                transactionCurrencyCode: LanguageLocale.getCountryISOCurrency(),
                isBopis: this.props.isBopis
            });

            if (result?.orderResponse) {
                const defaultPaymentNameToSend = paymentUtils.normalizePaymentName(defaultPaymentName || existingDefaultPayment);
                FrictionlessCheckoutBindings.setAnalyticsForOrderConfirmation(defaultPaymentNameToSend, isGooglePayDefaultPayment);
                this.onPlaceOrderSuccess(result.orderResponse);
            }
        } catch (err) {
            if (err?.reason === GooglePay.GOOGLE_PAY_ERROR_MESSAGES.incompleteCheckout) {
                return;
            }

            Sephora.logger.error('[GooglePay] checkout paysheet failed', err);

            const getText = LanguageLocale.getLocalisedDataFromResource(GooglePayEN, GooglePayFR);

            store.dispatch(
                Actions.showInfoModal({
                    isOpen: true,
                    isHtml: true,
                    showCloseButton: true,
                    title: getText('googlePayErrorTitle'),
                    message: getText('googlePayErrorMessage'),
                    buttonText: getText('googlePayErrorOkButton')
                })
            );
        }
    };

    isFreePaymentAvailable = () => {
        const {
            isKlarnaSelected,
            isKlarnaReady,
            isAfterpaySelected,
            isAfterpayReady,
            isPazeSelected,
            isPazeReady,
            isVenmoSelected,
            isVenmoReady,
            canCheckoutPaze
        } = this.props;
        const { isVenmoFlow } = this.state;

        const isFreePayment =
            (isKlarnaSelected && isKlarnaReady) ||
            (isAfterpaySelected && isAfterpayReady) ||
            (isPazeSelected && isPazeReady && canCheckoutPaze) ||
            (isVenmoSelected && isVenmoReady && !isVenmoFlow);

        return isFreePayment;
    };

    placeFreePaymentOrder = (event, freePaymentName) => {
        this.processPlaceOrderClickEvent(freePaymentName);
        const {
            getText, denialMessage, orderTotal, creditCardAmount, placeKlarnaOrder, placeAfterpayOrder, placePazeOrder, placeVenmoOrder
        } =
            this.props;

        const errorMessage = getText('authorizeErrorMessage', [freePaymentName]);

        const paymentActionsMapping = {
            Klarna: placeKlarnaOrder,
            Afterpay: placeAfterpayOrder,
            Paze: placePazeOrder,
            Venmo: placeVenmoOrder
        };

        const action = paymentActionsMapping[freePaymentName] || placePazeOrder;

        // Paze + Gift Card split payment (R4, US, flag-gated): authorize only the post-gift-card residual.
        const usePazeResidual = freePaymentName === 'Paze' && isFCFinalReleaseEnabled() && paymentUtils.isPazeSplitPaymentWithGCEnabled();

        action({
            errorMessage,
            denialMessage,
            transactionAmount: basketUtils.removeCurrency(usePazeResidual ? creditCardAmount || orderTotal : orderTotal)
        }).then(data => data && this.placeOrder(event, data));
    };

    processPlaceOrderClickEvent = freePaymentName => {
        const paymentName = freePaymentName.toLowerCase();
        const { defaultPaymentName, existingDefaultPayment } = this.props;
        const checkoutWithExistingDefaultPayment = paymentName === existingDefaultPayment;
        const actionInfo = `checkout:payment:continue with ${paymentName}`;
        const events = [anaConsts.Event.EVENT_71];

        if (defaultPaymentName || checkoutWithExistingDefaultPayment) {
            events.push('event252=1');
        }

        const eventData = {
            eventStrings: events,
            linkName: 'D=c55',
            actionInfo: actionInfo
        };

        const mostRecentAsyncLoadEvent = analyticsUtils.getMostRecentEvent('asyncPageLoad');

        if (mostRecentAsyncLoadEvent) {
            eventData.previousPage = mostRecentAsyncLoadEvent.eventInfo.attributes.previousPageName;
            const pageName = mostRecentAsyncLoadEvent?.eventInfo?.attributes?.pageName;
            eventData.pageName = pageName;

            if (pageName) {
                eventData.pageDetail = pageName.split(':')?.[1];
            }
        }

        processEvent.process(anaConsts.LINK_TRACKING_EVENT, { data: eventData });
    };

    processPlaceOrderBtnClick = (e, freePaymentName) => {
        const { isVenmoSelected } = this.props;
        const { isVenmoFlow } = this.state;
        const errors = checkForCheckoutSectionsCompleteness(this.props.orderDetails, false, this.props.sectionThatNeedsToBeConfirmed);
        const isVenmoPath = isVenmoSelected && !isVenmoFlow;

        // Validate payment section for all alternate payments
        if (Object.keys(errors).length > 0) {
            this.props.setCheckoutSectionErrors(errors);
            UI.scrollTo({ elementId: Object.keys(errors)[0], hasOffset: false });

            return;
        }

        isVenmoPath ? this.handleVenmoClick() : this.placeFreePaymentOrder(e, freePaymentName);
    };

    triggerSDUModal = (isCVVRequired = false) => {
        this.props.showSDUAgreementModal({
            isOpen: true,
            isBopis: this.props.isBopis,
            canCheckoutPaze: this.props.canCheckoutPaze,
            isSDUItemInBasket: this.props.isSDUItemInBasket,
            isCVVRequired
        });
    };

    closeSDUModal = () => {
        typeof this.props.closeSDUModal === 'function' && this.props.closeSDUModal();
    };

    hasCheckoutSectionErrors = () => {
        const isFCFinal = isFCFinalReleaseEnabled();
        const shouldValidateCVV =
            this.props.isCVVError &&
            !this.props.orderDetails?.priceInfo?.paypalAmount &&
            !this.isFreePaymentAvailable() &&
            !(isFCFinal && this.props.pendingCreditCard);

        const hasPendingCreditCard = isFCFinal && !!this.props.pendingCreditCard;

        if (!this.props.orderDetails?.header?.isComplete || shouldValidateCVV || this.props.sectionThatNeedsToBeConfirmed) {
            const errors = checkForCheckoutSectionsCompleteness(
                this.props.orderDetails,
                shouldValidateCVV,
                this.props.sectionThatNeedsToBeConfirmed,
                hasPendingCreditCard
            );

            if (Object.keys(errors).length > 0) {
                this.props.setCheckoutSectionErrors(errors);
                UI.scrollTo({ elementId: Object.keys(errors)[0], hasOffset: false });
                FrictionlessCheckoutBindings.errorTrackingBeforeAPICall(errors);

                return true;
            }
        }

        return false;
    };

    onPlaceOrderBtnClick = e => {
        if (!isFCFinalReleaseEnabled()) {
            this.placeOrder();

            return;
        }

        if (this.hasCheckoutSectionErrors()) {
            return;
        }

        if (this.props.placeOrderPreHook) {
            this.props.placeOrderPreHook(e).then(() => this.proceedAfterValidation());

            return;
        }

        this.proceedAfterValidation();
    };

    proceedAfterValidation = () => {
        const {
            isAutoReplenishBasket, acceptAutoReplenish, isSDUItemInBasket, sduAcceptTerms, isAddressMisMatch, pendingCreditCard, orderDetails
        } =
            this.props;

        if (isAddressMisMatch) {
            this.showGiftAddressWarning();

            return;
        }

        const isAgentTier3CVVRequired =
            isFCFinalReleaseEnabled() && agentAwareUtils.isAgentAwareSession() && agentAwareUtils.isAgentAuthorized(['3']);
        const isCVVRequired = !!pendingCreditCard && (FrictionlessUtils.showCVVModal(pendingCreditCard, orderDetails) || isAgentTier3CVVRequired);

        if (isAutoReplenishBasket && !acceptAutoReplenish) {
            this.props.showPlaceOrderTermsModal(true, !isSDUItemInBasket, isCVVRequired);

            return;
        }

        if (isSDUItemInBasket && !sduAcceptTerms) {
            this.triggerSDUModal(isCVVRequired);

            return;
        }

        this.placeOrder();
    };

    placeOrder = async (data = {}) => {
        const { pendingCreditCard, confirmCVVReason, orderDetails } = this.props;

        if (pendingCreditCard && isFCFinalReleaseEnabled()) {
            const isAgentTier3CVVRequired = agentAwareUtils.isAgentAwareSession() && agentAwareUtils.isAgentAuthorized(['3']);

            if (FrictionlessUtils.showCVVModal(pendingCreditCard, orderDetails) || isAgentTier3CVVRequired) {
                this.props.showConfirmCVVModal({
                    isOpen: true,
                    reason: confirmCVVReason,
                    successCallback: () => this.placeOrder(data)
                });

                return;
            } else {
                const requestData = FrictionlessUtils.setRequestDataForCreditCardUpdate(pendingCreditCard);
                decorators
                    .withInterstice(checkoutApi.updateCreditCardOnOrder, INTERSTICE_DELAY_MS)(requestData, 'select')
                    .then(creditCardData => {
                        if (creditCardData) {
                            this.props.orderMerge('order', 'bankRewardsValidPaymentsMessage', data.bankRewardsValidPaymentsMessage);
                        }

                        this.props.swapPaypalToCreditCard();
                        refreshCheckout()().then(() => {
                            this.props.clearPendingCreditCard();
                        });
                    })
                    .catch(errorData => {
                        ErrorsUtils.collectAndValidateBackEndErrors(errorData, this);
                    });
            }
        }

        if (this.hasCheckoutSectionErrors()) {
            this.props.isSDUItemInBasket && this.closeSDUModal();

            return;
        }

        const isSavePayPal = OrderUtils.isPayPalSaveToAccountChecked();
        const params = {
            savePayPal: this.props.isPaypalPaymentEnabled ? isSavePayPal : false,
            originOfOrder: this.props.originOfOrder
        };

        if (this.props.defaultPaymentName) {
            params.defaultPayment = this.props.defaultPaymentName;
        }

        // If this item comes from data, it means this order was placed using Klarna
        if (data.authorization_token) {
            params.klarnaAuthorizationToken = data.authorization_token;
        }

        // If this item comes from data, it means this order was placed using Paze (placeOrder in Paze Utils)
        if (data.paze) {
            params.paze = data.paze;
        }

        const linkshareInformation = await prepareCheckoutPlaceOrderLinkshareInformation({
            getAffiliateGatewayParams: () => analyticsUtils.getAffiliateGatewayParams()
        });

        if (linkshareInformation) {
            params.linkshareInformation = linkshareInformation;
        }

        //disable place order button just in case
        this.props.togglePlaceOrderDisabled(true);

        this.setState({
            isProcessingOrder: true,
            orderError: null
        });

        const birthdayGift = basketUtils.getBirthdayGift();

        decorators
            .withInterstice(
                checkoutApi.placeOrder,
                INTERSTICE_DELAY_MS
            )(params)
            .then(submittedDetails => {
                syncBopisDcAttributionAfterPlaceOrderSuccess({
                    isBopis: this.props.isBopis,
                    linkshareInformation
                });

                let isDefaultPaymentUsed;
                const { defaultPaymentMethodName, isDefaultPaymentBeingUsed, existingDefaultPayment } = this.props;
                const defaultPaymentNameToSend = defaultPaymentMethodName || existingDefaultPayment;

                if (this.isFreePaymentAvailable()) {
                    const freePaymentName = this.getFreePaymentName()?.toLowerCase();
                    const isUsingExistingDefaultPayment = freePaymentName === existingDefaultPayment;
                    isDefaultPaymentUsed = isUsingExistingDefaultPayment;
                } else {
                    isDefaultPaymentUsed = isDefaultPaymentBeingUsed;
                }

                FrictionlessCheckoutBindings.setAnalyticsForOrderConfirmation(defaultPaymentNameToSend, isDefaultPaymentUsed);

                FrictionlessCheckoutBindings.setPlaceOrderBtnClickanalytics({
                    isSuccess: true
                });

                if (birthdayGift) {
                    const { skuId, productId, brandName, productName } = birthdayGift.sku;
                    CheckoutBindings.birthdayGiftRedemption({ skuId, productId, brandName, productName });
                }

                const currentOrderDetails = store.getState().order.orderDetails;
                const defaultMethodId = Storage.local.getItem(LOCAL_STORAGE.CHECKOUT_DEFAULT_SHIPPING_METHOD_ID);
                const sameDayGroup = OrderUtils.getSameDayShippingGroup(currentOrderDetails);
                const hardGoodGroup = OrderUtils.getHardGoodShippingGroup(currentOrderDetails);
                const primaryShippingMethod = (sameDayGroup || hardGoodGroup)?.shippingMethod;
                const shippingSpeedPayload = buildShippingSpeedSOTPayload({
                    shippingMethod: primaryShippingMethod,
                    isBopis: this.props.isBopis,
                    defaultMethodId
                });

                if (this.props.isBopis) {
                    CheckoutBindings.placeBOPISOrder(shippingSpeedPayload);
                } else {
                    CheckoutBindings.placeStandardOrder(shippingSpeedPayload);
                }

                this.setState({
                    isProcessingOrder: false,
                    orderError: null
                });

                this.onPlaceOrderSuccess(submittedDetails);
            })
            .catch(this.onPlaceOrderFailure);
    };

    onPlaceOrderSuccess = submittedDetails => {
        this.props.isBopis && analyticsUtils.setNextPageData({ customData: { clickBopisPlaceOrder: true } });
        checkoutUtils.placeOrderSuccess(submittedDetails);
    };

    onPlaceOrderFailure = errorData => {
        const errors = getErrorStatesFromSubmitOrderAPI(errorData);

        if (isKlarnaFallbackAuthFailure(errorData)) {
            this.setState({ isProcessingOrder: false });

            return;
        }

        if (Object.keys(errors || {}).length > 0) {
            FrictionlessCheckoutBindings.setPlaceOrderBtnClickanalytics({
                isSuccess: false,
                hasErrorMessage: errorData?.errorMessages?.length > 0
            });
            UI.scrollTo({ elementId: Object.keys(errors)[0], hasOffset: false });
            this.props.setCheckoutSectionErrors(errors);
        }

        this.setState({
            isProcessingOrder: false,
            orderError: errorData?.errorMessages?.[0]
        });
    };

    showGiftAddressWarning = () => {
        const placeOrderCallback = this.placeOrder;
        const recipientName = this.props?.digitalGiftMsg?.recipientName;

        return this.props.showGiftAddressWarningModal({ isOpen: true, placeOrderCallback, recipientName });
    };

    renderPaymentButton = () => {
        const freePaymentName = this.getFreePaymentName();
        const { isProcessingOrder } = this.state;
        const {
            isApplePayFlow, isPaypalSelected, isPaypalReady, isGooglePaySelected, isGooglePayReady, orderTotal
        } = this.props;

        if (isGooglePaySelected && isGooglePayReady) {
            return (
                <GooglePayButton
                    onClickOverride={this.handleGooglePayClick}
                    useCheckoutPaymentsClient
                    isGooglePayPayment={GooglePay.TYPES.ENABLED}
                    data-at={Sephora.debug.dataAt('checkout_googlepay_btn')}
                />
            );
        }

        const { block = true, children = this.props.placeOrder, isBopis } = this.props;

        const getButtonAriaLabel = () => {
            const baseLabel = `${this.props.placeOrderFor} ${orderTotal}`;

            if (isProcessingOrder) {
                return `${baseLabel}. ${this.props.processingOrder}`;
            }

            if (this.getDisabledState()) {
                return `${baseLabel}. ${this.props.buttonDisabled}`;
            }

            return baseLabel;
        };

        // When PayPal is selected, ready, and the Braintree PayPal Checkout killswitch is ON,
        // replace the place order button with the PayPal SDK button.
        // Gating on isPaypalReady avoids mounting the SDK button before paypalCheckoutInstance
        // exists, which would cause renderPayPalButton to bail out and leave an empty container.
        if (isFCFinalReleaseEnabled() && isPaypalSelected && isPaypalReady && PayPalCheckout.isPayPalCheckoutEnabledForLocale()) {
            return <PayPalButton isBopis={isBopis} />;
        }

        // we dont want to show Apple pay as CTA in FC final release
        if (isApplePayFlow && !isFCFinalReleaseEnabled()) {
            return (
                <ApplePayButton
                    isApplePayPayment={true}
                    isBopis={isBopis}
                    block={block}
                />
            );
        } else if (this.isFreePaymentAvailable()) {
            return (
                <FreePaymentButton
                    onClick={e => this.processPlaceOrderBtnClick(e, freePaymentName)}
                    block={block}
                    minWidth={[null, '15em']}
                    data-at={Sephora.debug.dataAt('place_order_btn')}
                    freePaymentName={freePaymentName}
                    label={this.props.placeFreePayment}
                    orderTotal={orderTotal}
                    withText={this.props.withText}
                    forProcessingText={this.props.forProcessingText}
                    logoAlt={this.props.logoAlt}
                />
            );
        } else {
            const buttonText = isProcessingOrder ? this.props.processingOrder : children;

            return (
                <Button
                    variant={this.props.variant || 'special'}
                    disabled={this.getDisabledState()}
                    onClick={this.onPlaceOrderBtnClick}
                    block={block}
                    minWidth={[null, '15em']}
                    data-at={Sephora.debug.dataAt('place_order_btn')}
                    aria-label={getButtonAriaLabel()}
                >
                    {buttonText}
                </Button>
            );
        }
    };

    getDisabledState = () => {
        const disabledButton = this.props.disabled;
        const {
            isKlarnaSelected,
            isKlarnaReady,
            isAfterpaySelected,
            isAfterpayReady,
            isPazeSelected,
            isPazeReady,
            isVenmoSelected,
            isVenmoReady,
            isPaypalSelected,
            isPaypalReady,
            isGooglePaySelected,
            isGooglePayReady
        } = this.props;

        return (
            disabledButton ||
            (isKlarnaSelected && !isKlarnaReady) ||
            (isAfterpaySelected && !isAfterpayReady) ||
            (isPazeSelected && !isPazeReady) ||
            (isVenmoSelected && !isVenmoReady) ||
            (isPaypalSelected && !isPaypalReady) ||
            (isGooglePaySelected && !isGooglePayReady)
        );
    };

    getFreePaymentName = () => {
        const { isKlarnaSelected, isPazeSelected, isAfterpaySelected } = this.props;

        if (isKlarnaSelected) {
            return 'Klarna';
        } else if (isPazeSelected) {
            return 'Paze';
        } else if (isAfterpaySelected) {
            return 'Afterpay';
        } else {
            return 'Venmo';
        }
    };

    render() {
        return <>{this.renderPaymentButton()}</>;
    }
}

export default wrapComponent(PlaceOrderButton, 'PlaceOrderButton', true);
